/**
 * Zeitzonen-Arithmetik. **Die einzige Stelle im Repo, an der sie stattfinden darf.**
 *
 * Gespeichert wird durchgehend in UTC. Zeitzonen betreffen nur die Frage, wo ein
 * Tag oder Monat anfängt — und genau dort wird es unangenehm: In `Europe/Berlin`
 * hat der 29.03.2026 dreiundzwanzig Stunden und der 25.10.2026 fünfundzwanzig.
 * Wer einen Tag als „24 Stunden" rechnet, verschiebt an diesen beiden Tagen jede
 * Tagesauswertung um eine Stunde.
 *
 * Umgesetzt mit `Intl` aus der Standardbibliothek — keine Zeitzonen-Datenbank als
 * Abhängigkeit, damit die Zonendaten mit der Node-Version aktuell bleiben.
 */

/**
 * Versatz einer Zeitzone gegenüber UTC zum gegebenen Zeitpunkt, in Millisekunden.
 * Positiv östlich von Greenwich: `Europe/Berlin` liefert im Winter +3600000.
 */
export function timeZoneOffsetMs(instant: Date, timeZone: string): number {
  const parts = partsInZone(instant, timeZone);
  const asUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
    instant.getUTCMilliseconds(),
  );
  return asUtc - instant.getTime();
}

/** Kalenderfelder eines Zeitpunkts in einer Zeitzone. */
export interface ZonedParts {
  readonly year: number;
  readonly month: number;
  readonly day: number;
  readonly hour: number;
  readonly minute: number;
  readonly second: number;
}

const FORMATTERS = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  let formatter = FORMATTERS.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    FORMATTERS.set(timeZone, formatter);
  }
  return formatter;
}

/** Zerlegt einen Zeitpunkt in die Kalenderfelder einer Zeitzone. */
export function partsInZone(instant: Date, timeZone: string): ZonedParts {
  const parts = formatterFor(timeZone).formatToParts(instant);
  const get = (type: Intl.DateTimeFormatPartTypes): number => {
    const value = parts.find((part) => part.type === type)?.value;
    return value === undefined ? 0 : Number(value);
  };
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour: get("hour"),
    minute: get("minute"),
    second: get("second"),
  };
}

/**
 * Wandelt lokale Kalenderfelder in den zugehörigen UTC-Zeitpunkt.
 *
 * Zwei Näherungsschritte, weil der Versatz selbst vom Ergebnis abhängt: die erste
 * Schätzung kann bei einer Zeitumstellung um eine Stunde danebenliegen, die zweite
 * korrigiert sie. Existiert die lokale Zeit nicht (Sprungstunde im Frühjahr),
 * ergibt sich der Zeitpunkt unmittelbar nach dem Sprung — das ist die Konvention,
 * mit der Tagesgrenzen stabil bleiben.
 */
export function zonedPartsToUtc(parts: ZonedParts, timeZone: string): Date {
  const naive = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  );
  const firstGuess = new Date(naive - timeZoneOffsetMs(new Date(naive), timeZone));
  const correction = timeZoneOffsetMs(firstGuess, timeZone);
  return new Date(naive - correction);
}

/** Beginn des lokalen Kalendertags, als UTC-Zeitpunkt. */
export function startOfLocalDay(instant: Date, timeZone: string): Date {
  const parts = partsInZone(instant, timeZone);
  return zonedPartsToUtc({ ...parts, hour: 0, minute: 0, second: 0 }, timeZone);
}

/** Beginn des nächsten lokalen Kalendertags. Berücksichtigt 23- und 25-Stunden-Tage. */
export function startOfNextLocalDay(instant: Date, timeZone: string): Date {
  const start = startOfLocalDay(instant, timeZone);
  // 36 Stunden weiter liegt sicher im Folgetag, unabhängig von der Umstellung.
  const inNextDay = new Date(start.getTime() + 36 * 3_600_000);
  return startOfLocalDay(inNextDay, timeZone);
}

/** Beginn des lokalen Kalendermonats, als UTC-Zeitpunkt. */
export function startOfLocalMonth(instant: Date, timeZone: string): Date {
  const parts = partsInZone(instant, timeZone);
  return zonedPartsToUtc({ ...parts, day: 1, hour: 0, minute: 0, second: 0 }, timeZone);
}

export function startOfNextLocalMonth(instant: Date, timeZone: string): Date {
  const parts = partsInZone(instant, timeZone);
  const nextMonth = parts.month === 12 ? 1 : parts.month + 1;
  const year = parts.month === 12 ? parts.year + 1 : parts.year;
  return zonedPartsToUtc(
    { year, month: nextMonth, day: 1, hour: 0, minute: 0, second: 0 },
    timeZone,
  );
}

/** Die Blocklängen in Stunden, die einen Tag ohne Rest teilen — und damit an Mitternacht anstoßen. */
export const LOCAL_HOUR_BLOCKS = [1, 2, 3, 4, 6, 12] as const;
export type LocalHourBlock = (typeof LOCAL_HOUR_BLOCKS)[number];

/**
 * Beginn des lokalen Stundenblocks, in dem ein Zeitpunkt liegt — bei `hours = 3`
 * die Blöcke 0–3, 3–6, … 21–24 Uhr **nach der Uhr der Anlage**, nicht nach UTC:
 * Ein UTC-Raster aus drei Stunden läge in Berlin bei 1–4, 4–7 Uhr.
 *
 * An Umstellungstagen ist ein Block entsprechend kürzer oder länger: Im Frühjahr
 * fehlt die Stunde 2–3 (Block 0–3 ist zwei Stunden lang), im Herbst kommt sie
 * zweimal vor (Block 0–3 ist vier Stunden lang). Fällt der Blockbeginn in die
 * Sprungstunde, liegt er unmittelbar nach dem Sprung (wie `zonedPartsToUtc`).
 */
export function startOfLocalHourBlock(instant: Date, hours: LocalHourBlock, timeZone: string): Date {
  const parts = partsInZone(instant, timeZone);
  const hour = Math.floor(parts.hour / hours) * hours;
  return zonedPartsToUtc({ ...parts, hour, minute: 0, second: 0 }, timeZone);
}

/** Beginn des nächsten lokalen Stundenblocks; der letzte eines Tages endet an der nächsten Mitternacht. */
export function startOfNextLocalHourBlock(instant: Date, hours: LocalHourBlock, timeZone: string): Date {
  const parts = partsInZone(instant, timeZone);
  const next = (Math.floor(parts.hour / hours) + 1) * hours;
  if (next >= 24) return startOfNextLocalDay(instant, timeZone);
  return zonedPartsToUtc({ ...parts, hour: next, minute: 0, second: 0 }, timeZone);
}

/**
 * Länge eines lokalen Kalendertags in Stunden. An Umstellungstagen 23 oder 25.
 * Wer diese Funktion braucht, rechnet richtig; wer `* 24` schreibt, nicht.
 */
export function localDayLengthHours(instant: Date, timeZone: string): number {
  const start = startOfLocalDay(instant, timeZone);
  const end = startOfNextLocalDay(instant, timeZone);
  return (end.getTime() - start.getTime()) / 3_600_000;
}

/** Prüft, ob eine Zeitzonenkennung von der Laufzeit erkannt wird. */
export function isValidTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone });
    return true;
  } catch {
    return false;
  }
}

/* --------------------------------------------------------------------------
 * Kalenderzeiträume der Anlage: Tag, Woche, Monat, Jahr (HM-076)
 * ----------------------------------------------------------------------- */

/** Ein lokales Kalenderdatum, `YYYY-MM-DD` — kein Zeitpunkt. */
export type LocalDate = string;

const LOCAL_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Das Kalenderdatum, auf das ein Zeitpunkt in dieser Zeitzone fällt. */
export function localDateOf(instant: Date, timeZone: string): LocalDate {
  const parts = partsInZone(instant, timeZone);
  return `${String(parts.year).padStart(4, "0")}-${String(parts.month).padStart(2, "0")}-${String(parts.day).padStart(2, "0")}`;
}

/** Mitternacht dieses Kalendertags in der Zeitzone, als Zeitpunkt. */
export function startOfLocalDate(date: LocalDate, timeZone: string): Date {
  const match = LOCAL_DATE.exec(date);
  if (match === null) throw new Error(`Kein Kalenderdatum: ${date}`);
  return zonedPartsToUtc(
    { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]), hour: 0, minute: 0, second: 0 },
    timeZone,
  );
}

/**
 * Kalenderarithmetik auf einem Datum — ohne Zeitzone, denn ein Datum hat
 * keine. „Drei Tage später" ist eine Frage an den Kalender, nicht an die Uhr;
 * erst `startOfLocalDate` macht daraus wieder einen Zeitpunkt.
 */
export function shiftLocalDate(
  date: LocalDate,
  by: { readonly days?: number; readonly months?: number; readonly years?: number },
): LocalDate {
  const match = LOCAL_DATE.exec(date);
  if (match === null) throw new Error(`Kein Kalenderdatum: ${date}`);
  const shifted = new Date(
    Date.UTC(
      Number(match[1]) + (by.years ?? 0),
      Number(match[2]) - 1 + (by.months ?? 0),
      Number(match[3]) + (by.days ?? 0),
    ),
  );
  return shifted.toISOString().slice(0, 10);
}

/**
 * Der nächste Zeitpunkt, zu dem die Uhr in dieser Zeitzone `hour:minute` zeigt
 * — echt nach `after` (HM-165: „fertig um 7:00" an einem Ladepunkt).
 *
 * Heute, wenn die Uhrzeit dort noch bevorsteht, sonst morgen. Gerechnet wird am
 * Kalender und nicht mit `+ 24 h`: Liegt eine Zeitumstellung dazwischen, wäre
 * „morgen 7:00" sonst 6:00 oder 8:00. Eine Uhrzeit, die es an jenem Tag nicht
 * gibt (02:30 am Tag der Umstellung auf Sommerzeit), wird der Zeitpunkt
 * unmittelbar nach dem Sprung — die Konvention von `zonedPartsToUtc`.
 */
export function nextLocalTime(after: Date, hour: number, minute: number, timeZone: string): Date {
  const at = (date: LocalDate): Date => {
    const match = LOCAL_DATE.exec(date);
    if (match === null) throw new Error(`Kein Kalenderdatum: ${date}`);
    return zonedPartsToUtc(
      { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]), hour, minute, second: 0 },
      timeZone,
    );
  };
  const today = localDateOf(after, timeZone);
  const candidate = at(today);
  return candidate > after ? candidate : at(shiftLocalDate(today, { days: 1 }));
}

/**
 * Dieselbe lokale Uhrzeit, `days` Kalendertage früher — für den Vergleichstag
 * einer Ersatzwertbildung (HM-115).
 *
 * **Nicht `instant − days × 24 h`.** Liegt eine Zeitumstellung dazwischen, träfe
 * das die Viertelstunde eine Stunde daneben, und der Vergleichstag hätte die
 * Form des Morgens um eine Stunde verschoben. Gerechnet wird am Kalender, die
 * Uhrzeit bleibt stehen.
 *
 * `null`, wenn es die Uhrzeit an jenem Tag nicht gibt — 02:15 Uhr am Tag der
 * Umstellung auf Sommerzeit. Ein Ersatz dafür wäre geraten.
 */
export function sameLocalTimeDaysBefore(instant: Date, days: number, timeZone: string): Date | null {
  const parts = partsInZone(instant, timeZone);
  const date = shiftLocalDate(localDateOf(instant, timeZone), { days: -days });
  const match = LOCAL_DATE.exec(date);
  if (match === null) return null;
  const wanted = {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: parts.hour,
    minute: parts.minute,
    second: parts.second,
  };
  const result = zonedPartsToUtc(wanted, timeZone);
  const check = partsInZone(result, timeZone);
  return check.day === wanted.day && check.hour === wanted.hour && check.minute === wanted.minute
    ? result
    : null;
}

/**
 * Der Wochentag eines Kalendertags nach ISO 8601: 1 = Montag … 7 = Sonntag.
 *
 * Eine Frage an den Kalender und nicht an die Uhr — ein Datum hat keine
 * Zeitzone, und der 25. Oktober ist ein Sonntag, gleich wie lang er ist.
 */
export function isoWeekdayOfLocalDate(date: LocalDate): number {
  const match = LOCAL_DATE.exec(date);
  if (match === null) throw new Error(`Kein Kalenderdatum: ${date}`);
  const weekday = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))).getUTCDay();
  return weekday === 0 ? 7 : weekday;
}

/** Der Montag der Woche dieses Kalendertags — ebenfalls ohne Zeitzone. */
export function mondayOfLocalDate(date: LocalDate): LocalDate {
  return shiftLocalDate(date, { days: 1 - isoWeekdayOfLocalDate(date) });
}

/** Montag der Woche, in die dieser Zeitpunkt fällt. */
export function startOfLocalWeek(instant: Date, timeZone: string): Date {
  const date = localDateOf(instant, timeZone);
  const weekday = (new Date(`${date}T00:00:00Z`).getUTCDay() + 6) % 7;
  return startOfLocalDate(shiftLocalDate(date, { days: -weekday }), timeZone);
}

export function startOfNextLocalWeek(instant: Date, timeZone: string): Date {
  const monday = localDateOf(startOfLocalWeek(instant, timeZone), timeZone);
  return startOfLocalDate(shiftLocalDate(monday, { days: 7 }), timeZone);
}

export function startOfLocalYear(instant: Date, timeZone: string): Date {
  const parts = partsInZone(instant, timeZone);
  return zonedPartsToUtc({ ...parts, month: 1, day: 1, hour: 0, minute: 0, second: 0 }, timeZone);
}

export function startOfNextLocalYear(instant: Date, timeZone: string): Date {
  const parts = partsInZone(instant, timeZone);
  return zonedPartsToUtc(
    { year: parts.year + 1, month: 1, day: 1, hour: 0, minute: 0, second: 0 },
    timeZone,
  );
}

/** Die ISO-Kalenderwoche des lokalen Tags — Wochen beginnen montags, KW 1 enthält den 4. Januar. */
export function isoWeekOf(instant: Date, timeZone: string): number {
  const date = localDateOf(instant, timeZone);
  const day = new Date(`${date}T00:00:00Z`);
  const weekday = (day.getUTCDay() + 6) % 7;
  // Auf den Donnerstag derselben Woche — der entscheidet über das Jahr der Woche.
  day.setUTCDate(day.getUTCDate() - weekday + 3);
  const firstThursday = new Date(Date.UTC(day.getUTCFullYear(), 0, 4));
  const firstWeekday = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - firstWeekday + 3);
  return 1 + Math.round((day.getTime() - firstThursday.getTime()) / (7 * 86_400_000));
}

export const CALENDAR_PERIODS = ["day", "week", "month", "year"] as const;
export type CalendarPeriod = (typeof CALENDAR_PERIODS)[number];

export interface LocalPeriod {
  readonly kind: CalendarPeriod;
  /** Das erste Kalenderdatum des Zeitraums — der Anker, mit dem er sich benennen lässt. */
  readonly date: LocalDate;
  readonly from: Date;
  readonly to: Date;
}

/**
 * Der Kalenderzeitraum, in den ein Datum fällt — Tag, Woche, Monat oder Jahr
 * der Anlage. Ein Tag hat an den Umstellungstagen 23 oder 25 Stunden; das
 * steht hier nicht, weil `startOfNextLocalDay` es weiß (Regel 3).
 */
export function localPeriod(kind: CalendarPeriod, date: LocalDate, timeZone: string): LocalPeriod {
  const instant = startOfLocalDate(date, timeZone);
  const [from, to] =
    kind === "day"
      ? [startOfLocalDay(instant, timeZone), startOfNextLocalDay(instant, timeZone)]
      : kind === "week"
        ? [startOfLocalWeek(instant, timeZone), startOfNextLocalWeek(instant, timeZone)]
        : kind === "month"
          ? [startOfLocalMonth(instant, timeZone), startOfNextLocalMonth(instant, timeZone)]
          : [startOfLocalYear(instant, timeZone), startOfNextLocalYear(instant, timeZone)];
  return { kind, date: localDateOf(from, timeZone), from, to };
}

/** Der vorige bzw. nächste Zeitraum derselben Art — als Anker. */
export function shiftLocalPeriod(period: LocalPeriod, delta: number): LocalDate {
  switch (period.kind) {
    case "day":
      return shiftLocalDate(period.date, { days: delta });
    case "week":
      return shiftLocalDate(period.date, { days: 7 * delta });
    case "month":
      return shiftLocalDate(period.date, { months: delta });
    case "year":
      return shiftLocalDate(period.date, { years: delta });
  }
}
